import React, {useCallback, useContext, useState} from "react"
import Row from "@amzn/meridian/row";
import Table, {TableCell, TableRow,} from "@amzn/meridian/table"
import {RouteStageContext} from "../stageStatus/context/RouteStageContext";
import Pagination from "@amzn/meridian/pagination"
import {StageFilterContext} from "../stageStatus/context/StageFilterContext";
import Link from "@amzn/meridian/link";
import Tag from "@amzn/meridian/tag";
import Tooltip from "@amzn/meridian/tooltip";
import Input from "@amzn/meridian/input";
import {getDocumentURL} from "../handler/ParentDataHandler";
import {FormattedMessage} from "react-intl";
import {
    convertMillisecondsToHHMM,
    isARorSSD,
    naIfDefault,
    trimColValue,
    isRouteChevronEnabled,
    convertEpochToLocaleFormat, getBaseUrl
} from "../util/Util"
import {getCartStatusColor, getRouteStatusColor, getStageStatusColor} from "../util/EntityStatusManager";
import {CapabilityContext} from "../global/context/CapabilityContext";
import {AR_V2, DEFAULT_CAPABILITIES} from "../util/Constants";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small"
import chevronLeftSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-left-small"
import Icon from "@amzn/meridian/icon";
import {applyGlobalStageLocationTypeFilter, applyInlineStagingLocationFilter} from "../util/FilterUtil";
import Text from "@amzn/meridian/text";
import {AssociateContext} from "../associate/context/AssociateContextProvider";
import TableScrollWrapper from "../common/TableScrollWrapper";
import {useDeviceLayout} from "../common/context/DeviceLayoutContext";

const RouteViewTable = () => {

    const {configViewModel} = useContext(CapabilityContext)
    const {viewModel} = useContext(RouteStageContext)
    const {viewModelF} = useContext(StageFilterContext)
    const {associateModelDispatcher} = useContext(AssociateContext)
    const [expandedRoutes, setExpandedRoutes] = useState([])
    const {spacingInset_shorthand2} = useDeviceLayout()

    /** Pagination State **/
    const [currentPage, setCurrentPage] = useState(1)

    /** Sorting **/
    const [sortColumn, setSortColumn] = useState("routeSLA")
    const [sortDirection, setSortDirection] = useState("ascending")
    const onSort = useCallback(({sortColumn, sortDirection}) => {
        setSortDirection(sortDirection)
        setSortColumn(sortColumn)
    }, [])

    /** In Line Table Filter State **/
    const [routeCodeFilter, setRouteCodeFilter] = useState("")
    const [routeTypeFilter, setRouteTypeFilter] = useState("")
    const [routeStatus, setRouteStatus] = useState("")
    const [stageStatus, setStageStatus] = useState("")
    const [stageTime, setStageTime] = useState("")
    const [routeSLA, setRouteSLA] = useState("");
    const [location, setLocation] = useState("");
    const [driverFilter, setDriverFilter] = useState("");
    const [cartsFilter, setCartsFilter] = useState("");
    const [progressFilter, setProgressFilter] = useState("")
    const [associateFilter, setAssociateFilter] = useState("")
    const [stageDurationFilter, setStageDurationFilter] = useState("")


    /** Global Filters **/
    const filterOption = (route, filter, key) => {
        if (!viewModelF[filter] || viewModelF[filter].length === 0) {
            return true;
        }
        let result = false;
        viewModelF[filter].forEach(filterValue => {
            if ((filter === "status" && route[key] === filterValue)
                || (filter !== "status" && route[key].includes(filterValue))) {
                result = true;
            }
        })
        return result;
    }

    /** Inline Filters **/
    const inLineFilter = (route, filter, key) => {
        return filter.length === 0 || (route[key] != null && route[key].toString().toLowerCase().startsWith(filter.toLowerCase()));
    }

    const inLineContainsFilter = (route, filter, key) => {
        return filter.length === 0 || (route[key] != null && route[key].toString().toLowerCase().includes(filter.toLowerCase()));
    }

    let routeList = viewModel.routeList;
    routeList.sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) {
            return sortDirection === "ascending" ? -1 : 1
        }
        if (a[sortColumn] > b[sortColumn]) {
            return sortDirection === "ascending" ? 1 : -1
        }
        return 0
    })
    routeList = routeList
        .filter(route => filterOption(route, "cluster", "routeCode"))
        .filter(route => filterOption(route, "driverType", "routeType"))
        .filter(route => filterOption(route, "departure", "routeSLA"))
        .filter(route => filterOption(route, "status", "stageStatus"))
        .filter(route => applyGlobalStageLocationTypeFilter(viewModelF, route))
        .filter(route => inLineFilter(route, routeCodeFilter, "routeCode"))
        .filter(route => inLineContainsFilter(route, routeTypeFilter, "routeType"))
        .filter(route => inLineFilter(route, routeStatus, "routeStatus"))
        .filter(route => inLineFilter(route, stageStatus, "stageStatus"))
        .filter(route => inLineFilter(route, progressFilter, "stageProgress"))
        .filter(route => inLineFilter(route, associateFilter, "picker"))
        .filter(route => inLineFilter(route, stageTime, "stageTime"))
        .filter(route => inLineFilter(route, routeSLA, "routeSLA"))
        .filter(route => applyInlineStagingLocationFilter(route, location))
        .filter(route => inLineFilter(route, driverFilter, "driver"))
        .filter(route => inLineFilter(route, cartsFilter, "totalCarts"))
        .filter(route => inLineFilter(route, stageDurationFilter, "stageDuration"));


    const itemOnPage = 50;
    const pageSize = Math.ceil(routeList.length / itemOnPage);
    const startPage = (currentPage - 1) * itemOnPage;
    const endPage = (startPage + itemOnPage) > routeList.length - 1 ? routeList.length - 1 :
        (startPage + itemOnPage - 1);
    if (startPage > routeList.length) {
        setCurrentPage(1);
    }

    const onRouteClick = (route) => {
        getDocumentURL().then(result => {
            const params = [
                "Stage",
                naIfDefault(route.routeId),
                naIfDefault(encodeURIComponent(route.routeStatus)),
                naIfDefault(encodeURIComponent(route.stagingLocation)),
                naIfDefault(route.hazmatWeight),
                naIfDefault(route.picker),
                naIfDefault(route.routeCode),
                naIfDefault(route.stationCode),
            ]
            const url = new URL(result.replace("stage", "pick"));
            const shareableLink = "/routeDetail/" + params.join("/");
            url.searchParams.set("shareableLink", shareableLink);
            window.open(url.href, "_parent")
        });
    }

    const onLoadGuideSheetButtonClick = (route) => {
        getDocumentURL().then((result) => {
            let baseURL = getBaseUrl(result);
            const url = new URL(
                baseURL +
                "/rsws/generateLoadGuideSheet/stationCode/" +
                route.stationCode +
                "?routeId=" +
                route.routeId,
            );
            window.open(url.href, "_blank");

        });
    };

    const expandRoute = routeId => {
        if (expandedRoutes.indexOf(routeId) !== -1) {
            setExpandedRoutes(expandedRoutes.filter(expandedRoute => expandedRoute !== routeId));
        } else {
            setExpandedRoutes(expandedRoutes.concat(routeId));
        }
    }

    const isRouteExpanded = (routeId) => {
        return (expandedRoutes.indexOf(routeId) !== -1);
    }

    const renderHeaderRow = () => {
        return <TableRow>
            <TableCell alignmentHorizontal="right" sortColumn="routeSLA">
                {configViewModel.capability === AR_V2 ?
                    <FormattedMessage id="routeStageTable.dispatchTime" defaultMessage="Dispatch Time"/>
                    :
                    <FormattedMessage id="stage.stageByTime" defaultMessage="Stage by time"/>
                }
            </TableCell>
            <TableCell sortColumn="routeCode">
                <FormattedMessage id="routeStageTable.routeId" defaultMessage="Route ID"/>
            </TableCell>
            <TableCell sortColumn="stagingLocation">
                <FormattedMessage id="routeStageTable.location" defaultMessage="Location"/>
            </TableCell>
            {configViewModel.capability === AR_V2 ?
                <TableCell sortColumn="routeStatus">
                    <FormattedMessage id="routeStageTable.status" defaultMessage="Status"/>
                </TableCell>
                :
                <TableCell sortColumn="stageStatus">
                    <FormattedMessage id="routeStageTable.status" defaultMessage="Status"/>
                </TableCell>
            }

            {isARorSSD(configViewModel.capability) ?
                <TableCell alignmentHorizontal="right" sortColumn="stageProgress">
                    <FormattedMessage id="routeStageTable.progress" defaultMessage="Progress"/>
                </TableCell>
                : null
            }
            {isARorSSD(configViewModel.capability) ?
                <TableCell sortColumn="picker">
                    <FormattedMessage id="routeStageTable.associate" defaultMessage="Associate"/>
                </TableCell>
                : null
            }
            {DEFAULT_CAPABILITIES.includes(configViewModel.capability) ?
                <TableCell alignmentHorizontal="right" sortColumn="stageTime">
                    <FormattedMessage id="routeStageTable.stageTime" defaultMessage="Stage Time"/>
                </TableCell>
                : null
            }
            {isARorSSD(configViewModel.capability) ?
                <TableCell alignmentHorizontal="right" sortColumn="stageDuration">
                    <FormattedMessage id="routeStageTable.stageDuration" defaultMessage="Duration"/>
                </TableCell>
                : null
            }
            {!isARorSSD(configViewModel.capability) ?
                <TableCell alignmentHorizontal="right" sortColumn="totalCarts">
                    <FormattedMessage id="routeStageTable.carts" defaultMessage="Carts"/>
                </TableCell> : null}
            {configViewModel.capability === AR_V2 ?
                <TableCell sortColumn="driver">
                    <FormattedMessage id="routeStageTable.driver" defaultMessage="Driver"/>
                </TableCell>
                : null
            }
            <TableCell sortColumn="routeType">
                <FormattedMessage id="routeStageTable.type" defaultMessage="Type"/>
            </TableCell>
            {(configViewModel.stationConfig.loadGuideSheetDownloadEnabled) ?
                <TableCell alignmentHorizontal="right" sortColumn="totalCarts">
                    <FormattedMessage id="routeStageTable.loadSheet" defaultMessage="Load Sheet"/>
                </TableCell> : null
            }
        </TableRow>
    }


    const onAssociateClick = (picker) => {
        const associate = [picker]
        associateModelDispatcher({type: "SET_ASSOCIATE", data: associate});
    }

    const renderAssociateCol = (route) => {
        if (route.picker !== "...") {
            return <Link onClick={() => {
                onAssociateClick(route.picker)
            }} type="secondary">{route.picker}</Link>
        } else {
            return "-";
        }
    }

    const renderStageDuration = (route) => {
        if (route.stageError) {
            return  <Tooltip position="top" title={route.stageError}>
                <Text>{route.stageDuration}</Text>
            </Tooltip>
        }
        return route.stageDuration;
    }

    const renderInlineFilterRow = () => {
        return <TableRow>
            <TableCell>
                <Input type="text" size="small" value={routeSLA} onChange={setRouteSLA}/>
            </TableCell>
            <TableCell>
                <Input type="text" size="small" value={routeCodeFilter} onChange={setRouteCodeFilter}/>
            </TableCell>
            <TableCell>
                <Input type="text" size="small" value={location} onChange={setLocation}/>
            </TableCell>
            {configViewModel.capability === AR_V2 ?
                <TableCell>
                    <Input type="text" size="small" value={routeStatus} onChange={setRouteStatus}/>
                </TableCell>
                :
                <TableCell>
                    <Input type="text" size="small" value={stageStatus} onChange={setStageStatus}/>
                </TableCell>
            }
            {isARorSSD(configViewModel.capability) ?
                <TableCell>
                    <Input type="text" size="small" value={progressFilter}
                           onChange={setProgressFilter}/>
                </TableCell>
                : null
            }
            {isARorSSD(configViewModel.capability) ?
                <TableCell>
                    <Input type="text" size="small" value={associateFilter}
                           onChange={setAssociateFilter}/>
                </TableCell>
                : null
            }
            {DEFAULT_CAPABILITIES.includes(configViewModel.capability) ?
                <TableCell>
                    <Input type="text" size="small" value={stageTime} onChange={setStageTime}/>
                </TableCell>
                : null
            }
            {isARorSSD(configViewModel.capability) ?
                <TableCell>
                    <Input type="text" size="small" value={stageDurationFilter}
                           onChange={setStageDurationFilter}/>
                </TableCell>
                : null
            }
            {!isARorSSD(configViewModel.capability) ?
                <TableCell>
                    <Input type="text" size="small" value={cartsFilter} onChange={setCartsFilter}/>
                </TableCell> : null}
            {configViewModel.capability === AR_V2 ?
                <TableCell>
                    <Input type="text" size="small" value={driverFilter}
                           onChange={setDriverFilter}/>
                </TableCell>
                : null
            }
            <TableCell>
                <Input type="text" size="small" value={routeTypeFilter} onChange={setRouteTypeFilter}/>
            </TableCell>
        </TableRow>
    }

    const renderRouteRow = (route) => {
        return [<TableRow key={route.routeId}>
            <TableCell alignmentHorizontal="right">
                <Tooltip position={"right"} title={convertEpochToLocaleFormat(route.criticalPullTime)}>
                    <Text alignment={"right"}>{route.routeSLA}</Text>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Row>
                    {isRouteChevronEnabled(configViewModel.stationConfig) ?
                        <Link data-expand-link={route.routeId} onClick={() => expandRoute(route.routeId)}
                              type={"secondary"} size={"small"}>
                            {isRouteExpanded(route.routeId) ? <Icon tokens={chevronDownSmallTokens}/> :
                                <Icon tokens={chevronLeftSmallTokens}/>}
                        </Link>
                        : null
                    }
                    <Link onClick={() => {
                        onRouteClick(route)
                    }} type="secondary">{route.routeCode}</Link>
                </Row>
            </TableCell>
            <TableCell>
                <Tooltip position="top" title={route.stagingLocation}>
                    <Text>{trimColValue(route.stagingLocation, 16)}</Text>
                </Tooltip>
            </TableCell>
            {configViewModel.capability === AR_V2 ?
                <TableCell>
                    <Tooltip position="top" title={route.routeStatus}>
                        <Tag type={getRouteStatusColor(route.routeStatus)}>
                            {route.routeStatus && route.routeStatus.length > 11 ?
                                route.routeStatus.substr(0, 10) + "..."
                                : route.routeStatus
                            }
                        </Tag>
                    </Tooltip>
                </TableCell>
                :
                <TableCell>
                    <Tooltip position="top" title={route.stageStatus}>
                        <Tag type={getStageStatusColor(route.stageStatus)}>
                            {route.stageStatus && route.stageStatus.length > 11 ?
                                route.stageStatus.substr(0, 10) + "..."
                                : route.stageStatus
                            }
                        </Tag>
                    </Tooltip>
                </TableCell>
            }
            {isARorSSD(configViewModel.capability) ?
                <TableCell alignmentHorizontal="right">{route.stageProgress}</TableCell>
                : null
            }
            {isARorSSD(configViewModel.capability) ?
                <TableCell>
                    {renderAssociateCol(route)}
                </TableCell>
                : null
            }
            {DEFAULT_CAPABILITIES.includes(configViewModel.capability) ?
                <TableCell alignmentHorizontal="right">{convertMillisecondsToHHMM(route.stageTime)}</TableCell>
                : null
            }
            {isARorSSD(configViewModel.capability) ?
                <TableCell alignmentHorizontal="right">{renderStageDuration(route)}</TableCell>
                : null
            }
            {!isARorSSD(configViewModel.capability) ?
                <TableCell alignmentHorizontal="right">{route.totalCarts}</TableCell> : null}
            {configViewModel.capability === AR_V2 ?
                <TableCell>{route.driver}</TableCell>
                : null
            }
            <TableCell>{route.routeType}</TableCell>
            {(configViewModel.stationConfig.loadGuideSheetDownloadEnabled) ?
                <TableCell>
                    <Link onClick={() => {
                        onLoadGuideSheetButtonClick(route)
                    }} type="secondary"><FormattedMessage id="routeStageTable.download" defaultMessage="Download"/></Link>
                </TableCell> : null
            }
        </TableRow>]
            .concat(renderCartRows(route));
    }

    const renderCartRows = (route) => {
        if (!isRouteExpanded(route.routeId)
            || !(Array.isArray(route.carts) && route.carts.length)
            || !isRouteChevronEnabled(configViewModel.stationConfig)) {
            return [];
        }
        return route.carts.map(cart => renderCartRow(route, cart))
    }

    const renderCartRow = (route, cart) => {
        return <TableRow key={`${route.routeCode}_${cart.cartId}`} backgroundColor={"secondary"}>
            <TableCell/>
            <TableCell>
                {cart.cartId}
            </TableCell>
            <TableCell>{cart.location}</TableCell>
            <TableCell>
                <Tooltip position="top" title={cart.status}>
                    <Tag type={getCartStatusColor(cart.status)}>
                        {trimColValue(cart.status, 11)}
                    </Tag>
                </Tooltip>
            </TableCell>
            <TableCell alignmentHorizontal="right">{convertMillisecondsToHHMM(cart.stageTime)}</TableCell>
            <TableCell/>
            <TableCell/>
        </TableRow>
    }

    return (
        <div>
            <TableScrollWrapper>
                <Row width="100%" widths="fill" spacingInset={spacingInset_shorthand2} className="enable-scroll-x">
                    <div>
                        <Table
                            showStripes={DEFAULT_CAPABILITIES.includes(configViewModel.capability) && !isRouteChevronEnabled(configViewModel.stationConfig)}
                            showDividers={DEFAULT_CAPABILITIES.includes(configViewModel.capability)}
                            headerRows={1}
                            headerColumns={1}
                            fixHeaderRows={true}
                            stickyHeaderColumn={true}
                            stickyHeaderRow={true}
                            spacing={"small"}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSort={onSort}>
                            {renderHeaderRow()}
                            {renderInlineFilterRow()}
                            {routeList.map((route, key) =>
                                ((key >= startPage && key <= endPage) ?
                                    renderRouteRow(route)
                                    : null))
                            }
                        </Table>

                        {routeList.length > 0 ?
                            <Row width="100%" alignmentHorizontal="center" spacingInset="medium none none">
                                <Pagination
                                    numberOfPages={pageSize}
                                    onChange={setCurrentPage}
                                    currentPage={currentPage}/>
                            </Row> : null}

                    </div>
                </Row>
            </TableScrollWrapper>
        </div>
    )
}

export default RouteViewTable


// WEBPACK FOOTER //
// ./src/components/routeStageStatus/RouteViewTable.js